import jsPDF from 'jspdf';
import { ScrapedAd, CIAdDetail } from './types';
import { formatEnum, wrapText, loadImageAsDataUrl } from './pdfHelpers';

export default async function generateAdPdf(
  ad: ScrapedAd,
  transcript: string | null,
  ciDetail: CIAdDetail | null,
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  const checkPageBreak = (needed: number) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // ── Title ──
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Creative Summary', margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, y);
  doc.setTextColor(0);
  y += 10;

  // ── Thumbnail ──
  if (ad.videoThumbnailUrl) {
    const imgData = await loadImageAsDataUrl(ad.videoThumbnailUrl);
    if (imgData) {
      const imgWidth = contentWidth * 0.5;
      const imgHeight = imgWidth * 0.5625; // 16:9 aspect ratio
      checkPageBreak(imgHeight + 5);
      try {
        doc.addImage(imgData, 'JPEG', margin, y, imgWidth, imgHeight);
        y += imgHeight + 5;
      } catch {
        // Image loading can fail for CORS — skip silently
      }
    }
  }

  // ── Ad Metadata ──
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(ad.pageName || 'Unknown Page', margin, y);
  y += 6;

  if (ad.headline) {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const lines = wrapText(doc, ad.headline, contentWidth);
    checkPageBreak(lines.length * 5);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 2;
  }

  // Metadata chips
  doc.setFontSize(9);
  doc.setTextColor(80);
  const metaParts: string[] = [];
  if (ad.cta) metaParts.push(`CTA: ${ad.cta}`);
  if (ad.platforms) metaParts.push(`Platform: ${ad.platforms}`);
  if (ad.startDate) metaParts.push(`Started: ${ad.startDate}`);
  if (ad.impressionRank) metaParts.push(`Rank: #${ad.impressionRank}`);
  if (metaParts.length > 0) {
    doc.text(metaParts.join('  |  '), margin, y);
    y += 5;
  }
  if (ad.landingPageUrl) {
    doc.setTextColor(40, 80, 160);
    const urlLines = wrapText(doc, ad.landingPageUrl, contentWidth);
    doc.text(urlLines, margin, y);
    y += urlLines.length * 4 + 2;
  }
  if (ciDetail?.archivedVideoShareUrl) {
    doc.setTextColor(40, 80, 160);
    const videoLines = wrapText(doc, `Video link (30-day): ${ciDetail.archivedVideoShareUrl}`, contentWidth);
    doc.text(videoLines, margin, y);
    y += videoLines.length * 4 + 2;
  }
  doc.setTextColor(0);

  if (ad.caption) {
    y += 2;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    const captionLines = wrapText(doc, ad.caption, contentWidth);
    checkPageBreak(captionLines.length * 4 + 2);
    doc.text(captionLines, margin, y);
    y += captionLines.length * 4 + 2;
    doc.setFont('helvetica', 'normal');
  }

  y += 5;

  // ── Divider ──
  const drawDivider = () => {
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;
  };

  // ── Transcript ──
  if (transcript) {
    drawDivider();
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Transcript', margin, y);
    y += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const transcriptLines = wrapText(doc, transcript, contentWidth);
    for (const line of transcriptLines) {
      checkPageBreak(4);
      doc.text(line, margin, y);
      y += 4;
    }
    y += 3;
  }

  // ── Extraction Features ──
  if (ciDetail?.features) {
    drawDivider();
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Creative Features', margin, y);
    y += 7;

    const f = ciDetail.features;
    const featureGrid: [string, string | null][] = [
      ['Awareness', f.awarenessLevel],
      ['Hook Device', f.hookDevice],
      ['Persuasion Angle', f.persuasionAngle],
      ['Proof Type', f.proofType],
      ['Narrative Structure', f.narrativeStructure],
      ['Emotion', f.primaryEmotion],
      ['Presenter', f.presenter],
      ['Production Format', f.productionFormat],
      ['Tone', f.tone],
      ['Offer Type', f.offerType],
      ['CTA Type', f.ctaType],
    ];

    doc.setFontSize(9);
    const colWidth = contentWidth / 4;
    let col = 0;
    for (const [label, val] of featureGrid) {
      if (!val) continue;
      const xPos = margin + (col % 4) * colWidth;
      if (col % 4 === 0 && col > 0) {
        y += 9;
        checkPageBreak(9);
      }
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120);
      doc.text(label, xPos, y);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      doc.text(formatEnum(val), xPos, y + 4);
      doc.setFont('helvetica', 'normal');
      col++;
    }
    y += 12;

    // Free-text summaries
    const summaries: [string, string | null][] = [
      ['Hook', f.hookSummary],
      ['Problem', f.problemSummary],
      ['Core Benefit', f.coreBenefit],
      ['Unique Angle', f.uniqueAngle],
      ['Strongest Moment', f.strongestMomentSummary],
    ];

    for (const [label, val] of summaries) {
      if (!val) continue;
      checkPageBreak(12);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80);
      doc.text(label, margin, y);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'normal');
      y += 4;
      const lines = wrapText(doc, val, contentWidth);
      for (const line of lines) {
        checkPageBreak(4);
        doc.text(line, margin, y);
        y += 4;
      }
      y += 2;
    }
  }

  // ── Segments ──
  if (ciDetail?.segments && ciDetail.segments.length > 0) {
    drawDivider();
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Ad Segments', margin, y);
    y += 7;

    for (const seg of ciDetail.segments) {
      checkPageBreak(20);

      // Segment header
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      let header = `${seg.segmentOrder}. ${formatEnum(seg.segmentRole)}`;
      if (seg.startTime != null && seg.endTime != null) {
        header += ` (${seg.startTime.toFixed(1)}s - ${seg.endTime.toFixed(1)}s)`;
      }
      doc.text(header, margin + 3, y);
      y += 5;

      // Summary
      if (seg.summary) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const lines = wrapText(doc, seg.summary, contentWidth - 3);
        for (const line of lines) {
          checkPageBreak(4);
          doc.text(line, margin + 3, y);
          y += 4;
        }
      }

      // Transcript excerpt
      if (seg.transcriptExcerpt) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(80);
        const lines = wrapText(doc, `"${seg.transcriptExcerpt}"`, contentWidth - 3);
        for (const line of lines) {
          checkPageBreak(4);
          doc.text(line, margin + 3, y);
          y += 3.5;
        }
        doc.setTextColor(0);
        doc.setFont('helvetica', 'normal');
      }

      // Metadata
      const segMeta: string[] = [];
      if (seg.emotionalTone) segMeta.push(`Tone: ${formatEnum(seg.emotionalTone)}`);
      if (seg.pacing) segMeta.push(`Pacing: ${formatEnum(seg.pacing)}`);
      if (seg.shotType) segMeta.push(`Shot: ${seg.shotType}`);
      if (segMeta.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(segMeta.join('  |  '), margin + 3, y);
        doc.setTextColor(0);
        y += 4;
      }

      y += 3;
    }
  }

  // ── Beats ──
  if (ciDetail?.beats && ciDetail.beats.length > 0) {
    drawDivider();
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Persuasion Beats', margin, y);
    y += 7;

    for (const beat of ciDetail.beats) {
      checkPageBreak(15);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`${beat.beatOrder}. ${beat.beatName || '-'} [${formatEnum(beat.beatType)}]`, margin + 3, y);
      y += 4;

      if (beat.beatDescription) {
        doc.setFont('helvetica', 'normal');
        const lines = wrapText(doc, beat.beatDescription, contentWidth - 3);
        for (const line of lines) {
          checkPageBreak(4);
          doc.text(line, margin + 3, y);
          y += 4;
        }
      }
      y += 2;
    }
  }

  // ── Footer on each page ──
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(160);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    doc.text(`Ad ID: ${ad.adId}`, margin, pageHeight - 8);
  }

  // Generate filename
  const pageName = (ad.pageName || 'ad').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  const date = new Date().toISOString().slice(0, 10);
  doc.save(`${pageName}-creative-summary-${date}.pdf`);
}
